import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { toPixelRect } from './toPixelRect';
import type { ESignFieldPosition, ESignPageDimensions } from './types';

type ESignFieldOverlayFillVariant = 'default' | 'solid' | 'outline';
const FILLED_OVERLAY_ALPHA = 0.18;

interface ESignFieldOverlayProps {
  position: ESignFieldPosition;
  pageDimensions: ESignPageDimensions;
  children: React.ReactNode;
  color: string;
  hoverContent?: React.ReactNode;
  isCheckbox?: boolean;
  isSelected?: boolean;
  fillVariant?: ESignFieldOverlayFillVariant;
  dataTestId?: string;
  dataFieldClientId?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>;
  style?: React.CSSProperties;
}

function getBackgroundColor({
  color,
  fillVariant,
  isCheckbox
}: {
  color: string;
  fillVariant: ESignFieldOverlayFillVariant;
  isCheckbox: boolean;
}) {
  if (fillVariant === 'solid') {
    return alpha(color, FILLED_OVERLAY_ALPHA);
  }
  if (fillVariant === 'outline') {
    return 'transparent';
  }
  return isCheckbox ? 'transparent' : alpha(color, FILLED_OVERLAY_ALPHA);
}

export function ESignFieldOverlay({
  position,
  pageDimensions,
  children,
  color,
  hoverContent,
  isCheckbox = false,
  isSelected = false,
  fillVariant = 'default',
  dataTestId,
  dataFieldClientId,
  onClick,
  onMouseDown,
  onTouchStart,
  style
}: ESignFieldOverlayProps) {
  const theme = useTheme();
  const pixelRect = useMemo(
    () => toPixelRect(position, pageDimensions),
    [position, pageDimensions]
  );
  const usesEditorSelectionTreatment = Boolean(dataFieldClientId);
  const selectedShadow =
    isSelected && usesEditorSelectionTreatment
      ? [
          `0 0 0 2px ${theme.palette.common.white}`,
          `0 4px 12px ${alpha(theme.palette.common.black, 0.24)}`
        ].join(', ')
      : undefined;
  const selectedOutline = !isSelected
    ? ''
    : usesEditorSelectionTreatment
      ? `3px solid ${theme.palette.text.primary}`
      : `2px solid ${color}`;
  const overlay = (
    <div
      data-testid={dataTestId}
      data-esign-template-field-client-id={dataFieldClientId}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      style={{
        position: 'absolute',
        left: pixelRect.left,
        top: pixelRect.top,
        width: pixelRect.width,
        height: pixelRect.height,
        pointerEvents: 'auto',
        border: `2px solid ${color}`,
        borderRadius: isCheckbox ? 0 : 2,
        backgroundColor: getBackgroundColor({
          color,
          fillVariant,
          isCheckbox
        }),
        outline: selectedOutline,
        outlineOffset: usesEditorSelectionTreatment ? 2 : 0,
        boxShadow: selectedShadow,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        transition:
          'box-shadow 120ms ease, border-color 120ms ease, background-color 120ms ease',
        ...style
      }}
    >
      {children}
      {isSelected && usesEditorSelectionTreatment && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -7,
            right: -7,
            width: 12,
            height: 12,
            borderRadius: isCheckbox ? 0 : 2,
            backgroundColor: theme.palette.text.primary,
            border: `2px solid ${theme.palette.common.white}`,
            boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.24)}`,
            pointerEvents: 'none'
          }}
        />
      )}
    </div>
  );

  if (!hoverContent) {
    return overlay;
  }

  return (
    <Tooltip
      arrow
      disableInteractive
      placement="top"
      title={hoverContent}
      slotProps={{
        tooltip: {
          sx: {
            borderRadius: '8px',
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.24)}`,
            fontSize: 12,
            fontWeight: 500,
            px: 1.5,
            py: 1
          }
        }
      }}
    >
      {overlay}
    </Tooltip>
  );
}
